import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";

import { List, Paper, Popper } from "@mui/material";

import { LastOpenedClientResponse } from "@/api";
import { CLIENT_SMART_SEARCH_ARIA } from "@/common/components/client-smart-search/ClientSmartSearch.utils";
import { useClientsSearchContext } from "@/common/contexts/clients-search-context/ClientsSearchContext";
import { useCurrentUserContext } from "@/common/contexts/current-user-context/CurrentUserContext";
import { mapClientsFilterFromSearchParams } from "@/pages/clients/clients/Clients.utils";
import { clientPath } from "@/router/ClientRoutes";

import { ClientListItem } from "./components/client-list-item/ClientListItem";
import { MyClientsListItem } from "./components/my-clients-list-item/MyClientsListItem";

interface Props {
  clients: LastOpenedClientResponse[];
  anchorEl: HTMLElement | null;
  open: boolean;
  width?: number;
  highlightedIndex: number;
}

const LIST_MAX_HEIGHT = 270;

export function ClientListPopper({ clients, anchorEl, open, width, highlightedIndex }: Props) {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUserContext();
  const itemRefs = useRef<Array<HTMLLIElement | null>>([]);

  const { invalidateClientsQuery, handleFiltersUpdate } = useClientsSearchContext();

  useEffect(() => {
    const currentItem = itemRefs.current[highlightedIndex];
    if (currentItem) {
      currentItem.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [highlightedIndex]);

  const navigateToMyClients = async () => {
    const searchParams = clientPath.my(currentUser.counselorId);
    await invalidateClientsQuery();
    await handleFiltersUpdate(mapClientsFilterFromSearchParams(searchParams));
  };

  const navigateToClient = async (id: string) => {
    const path = clientPath.byId({ id: id });
    await navigate(path);
  };

  return (
    <Popper open={open} anchorEl={anchorEl} placement="bottom-start" disablePortal sx={{ zIndex: 1300 }}>
      <Paper sx={{ width, overflowX: "auto" }}>
        <List
          dense
          id={CLIENT_SMART_SEARCH_ARIA.listboxId}
          sx={{
            overflowY: "auto",
            maxHeight: LIST_MAX_HEIGHT,
            display: "flex",
            flexDirection: "column",
            whiteSpace: "nowrap",
            minWidth: "max-content",
          }}
        >
          <MyClientsListItem
            ref={(el) => {
              itemRefs.current[0] = el;
            }}
            id={CLIENT_SMART_SEARCH_ARIA.getOptionId(0)}
            ariaSelected={highlightedIndex === 0}
            highlighted={highlightedIndex === 0}
            onClick={() => void navigateToMyClients()}
          />
          {clients.map((client, idx) => {
            const clientIndex = idx + 1;
            return (
              <ClientListItem
                key={client.id}
                client={client}
                ref={(el) => {
                  itemRefs.current[clientIndex] = el;
                }}
                id={CLIENT_SMART_SEARCH_ARIA.getOptionId(clientIndex)}
                ariaSelected={highlightedIndex === clientIndex}
                highlighted={highlightedIndex === clientIndex}
                onClick={() => void navigateToClient(client.id)}
              />
            );
          })}
        </List>
      </Paper>
    </Popper>
  );
}
